import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../lib/axios';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import DateInput from '../../components/ui/DateInput';
import Select from '../../components/ui/Select';
import { Card, CardHeader, CardContent } from '../../components/ui/Card';
import { Plus, FileText, ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';

const NewAssessment = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [formData, setFormData] = useState({
    client_id: '',
  });
  const [errors, setErrors] = useState({});

  // Add Client modal state
  const [addClientOpen, setAddClientOpen] = useState(false);
  const [clientForm, setClientForm] = useState({
    full_name: '',
    date_of_birth: '',
    email: '',
    cellphone: '',
    client_code: '',
  });
  const [clientErrors, setClientErrors] = useState({});
  const [savingClient, setSavingClient] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setLoadingClients(true);
      const response = await api.get('/clients');
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    } finally {
      setLoadingClients(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.client_id) newErrors.client_id = 'Please select a client';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateClientForm = () => {
    const newErrors = {};
    if (!clientForm.full_name.trim()) newErrors.full_name = 'Name is required';
    if (clientForm.date_of_birth) {
      const birthDate = new Date(clientForm.date_of_birth);
      const today = new Date();
      let calcAge = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) calcAge--;
      if (calcAge < 18 || calcAge > 100) newErrors.date_of_birth = 'Client must be between 18 and 100 years old';
    }
    if (!clientForm.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientForm.email)) newErrors.email = 'Invalid email format';
    if (!clientForm.cellphone.trim()) newErrors.cellphone = 'Cellphone is required';
    setClientErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddClient = async () => {
    if (!validateClientForm()) return;
    setSavingClient(true);
    try {
      const payload = {
        ...clientForm,
        date_of_birth: clientForm.date_of_birth,
        client_code: clientForm.client_code.trim() || null,
      };
      const res = await api.post('/clients', payload);
      // Refresh client list and auto-select the new client
      await fetchClients();
      setFormData({ ...formData, client_id: res.data.id });
      setAddClientOpen(false);
      setClientForm({ full_name: '', date_of_birth: '', email: '', cellphone: '', client_code: '' });
      setClientErrors({});
    } catch (error) {
      const msg = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to save client';
      setClientErrors({ submit: msg });
    } finally {
      setSavingClient(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const response = await api.post('/assessments', formData);
      navigate(`/assessments/${response.data.id}`);
    } catch (error) {
      setErrors({ submit: error.response?.data?.error || error.message || 'Failed to create assessment' });
    }
  };

  const clientOptions = clients.map(client => ({
    value: client.id,
    label: `${client.full_name} (${client.email})`
  }));

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.surface }}>
      <header className="border-b" style={{ backgroundColor: theme.card, borderColor: theme.border }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold" style={{ color: theme.text }}>New Investment Assessment</h1>
              <p className="text-sm" style={{ color: theme.textSecondary }}>Select a client to begin</p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Cancel
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 rounded-lg" style={{ backgroundColor: `${theme.primary}20` }}>
                <FileText className="w-6 h-6" style={{ color: theme.primary }} />
              </div>
              <h1 className="text-xl font-bold" style={{ color: theme.text }}>
                Create New Assessment
              </h1>
            </div>
            <p className="text-sm" style={{ color: theme.textSecondary }}>
              Choose an existing client or add a new one.
            </p>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} noValidate className="space-y-6">
              {errors.submit && <p className="text-sm text-red-600 dark:text-red-400">{errors.submit}</p>}
              
              {loadingClients ? (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin" style={{ color: theme.primary }} />
                  <p className="text-sm" style={{ color: theme.textSecondary }}>Loading clients...</p>
                </div>
              ) : clients.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm mb-4" style={{ color: theme.textSecondary }}>No clients found. Add one below to get started.</p>
                </div>
              ) : (
                <Select
                  label="Select Client"
                  options={clientOptions}
                  value={formData.client_id}
                  onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                  error={errors.client_id}
                  required
                />
              )}

              {/* Add New Client button */}
              <Button type="button" variant="outline" onClick={() => { setClientForm({ full_name: '', date_of_birth: '', email: '', cellphone: '', client_code: '' }); setClientErrors({}); setAddClientOpen(true); }}>
                <Plus className="w-4 h-4 mr-2" /> Add New Client
              </Button>

              <div className="pt-4 flex justify-end">
                <Button type="submit" disabled={loadingClients || !formData.client_id}>
                  Create Assessment <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>

      {/* Add Client Modal */}
      {addClientOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="max-w-lg w-full">
            <Card>
              <CardHeader>
                <h2 className="text-2xl font-bold" style={{ color: theme.text }}>Add New Client</h2>
              </CardHeader>
              <CardContent>
                <form onSubmit={(e) => { e.preventDefault(); handleAddClient(); }} className="space-y-4">
                  {clientErrors.submit && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800"><p className="text-sm text-red-800 dark:text-red-200">{clientErrors.submit}</p></div>}
                  
                  <Input label="Full Name" value={clientForm.full_name} onChange={(e) => setClientForm({ ...clientForm, full_name: e.target.value })} error={clientErrors.full_name} required />
                  <div className="grid grid-cols-2 gap-4">
                    <DateInput label="Date of Birth (optional)" value={clientForm.date_of_birth} onChange={(iso) => setClientForm({ ...clientForm, date_of_birth: iso })} error={clientErrors.date_of_birth} />
                    <Input label="Client Code" value={clientForm.client_code} onChange={(e) => setClientForm({ ...clientForm, client_code: e.target.value })} />
                  </div>
                  <Input label="Email" type="email" value={clientForm.email} onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })} error={clientErrors.email} required />
                  <Input label="Cellphone" value={clientForm.cellphone} onChange={(e) => setClientForm({ ...clientForm, cellphone: e.target.value })} error={clientErrors.cellphone} required />

                  <div className="pt-4 flex justify-end space-x-3">
                    <Button type="button" variant="secondary" onClick={() => setAddClientOpen(false)}>Cancel</Button>
                    <Button type="submit" disabled={savingClient}>
                      {savingClient ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Save Client
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewAssessment;
